<script setup lang="ts">
	import PageHeader from "@/components/Navigation/PageHeader.vue";
	import { computed, h, ref, watchEffect } from "vue";
	import {
		useAuditTrailsActiveConnections,
		useAuditTrailsEndActiveConnection,
	} from "@/composables/audit-trails-api/audit-trails";
	import { useAuditTrailsHttp } from "@/composables/audit-trails-api/http-audit-trails";
	import {
		AuditTrailsType,
		DeleteTaskResponse,
	} from "@/composables/api-models";
	import ListConnection from "@/components/RemoteAccess/ListConnection.vue";
	import { AuditTrailsPageType } from "@/composables/audit-trails-api";
	import { ConfirmDialog } from "@ptx-autonomous/libs/confirm";
	import EndConnection from "@/components/AuditTrails/EndConnection.vue";
	import Disclaimer from "@/components/AuditTrails/Disclaimer.vue";
	import { useRouter } from "vue-router";
	import {
		useMachineCertificateHttp,
		useMultiMachineCredentials,
	} from "@/composables/machie-certificate-api";
	const router = useRouter();
	// http request to get active connections
	const machineCertificateHttp = useMachineCertificateHttp();
	const auditTrailsHttp = useAuditTrailsHttp();
	const activeConnection = useAuditTrailsActiveConnections(auditTrailsHttp);
	const useEndConnection = useAuditTrailsEndActiveConnection(auditTrailsHttp);
	const type = AuditTrailsPageType.Active;
	const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);
	const deleteDialog = ref<InstanceType<typeof EndConnection>>();
	const disclaimerDialog = ref<InstanceType<typeof Disclaimer>>();

	const machineIds = computed(() => {
		return (
			activeConnection.data.value?.map(connection => connection.MachineId!) ||
			[]
		);
	});

	const { data: machineIdData } = useMultiMachineCredentials(
		machineCertificateHttp,
		machineIds,
	);

	async function TerminateConnection(remoteId: string) {
		remoteId = remoteId.split("/")[1];
		const result = await confirmDialog.value?.open({
			title: "Confirm Terminate Connection",
			text:
				"Are you sure you want to terminate this connection (" +
				remoteId +
				")?",
			okText: "Terminate",
			cancelText: "Cancel",
		});
		if (result) {
			try {
				console.log("Terminate connection: ", remoteId);

				const response = await useEndConnection.mutateAsync({
					remoteId,
				});
				console.log("response", response.data);
				deleteDialog.value?.openDialog(response.data);
				return true;
			} catch (error) {
				console.error(error);
				return false;
			}
		}
	}
	function openDisclaimerDialog() {
		disclaimerDialog.value?.openDialog();
	}
	const filteredConnections = ref<
		(AuditTrailsType & { organizationName?: string; organizationId?: string })[]
	>([]);

	const errorMessage = ref<string | undefined>();
	const activeConnectionWithOrganization = computed(() => {
		if (
			activeConnection.data.value &&
			machineIdData.value &&
			machineIdData.value.length > 0
		) {
			return activeConnection.data.value.map(connection => {
				const machine = machineIdData.value?.find(
					m => m.MachineId === connection.MachineId,
				);
				return {
					...connection,
					organizationName: machine?.OrganizationName || "",
					organizationId: machine?.OrganizationId || "",
				};
			});
		} else {
			return [];
		}
	});

	watchEffect(() => {
		const machineId = router.currentRoute.value.query?.machineId as
			| string
			| undefined;
		console.log("Filtering for machineId:", machineId);
		if (machineId && activeConnection.data.value) {
			filteredConnections.value = activeConnectionWithOrganization.value.filter(
				connection => connection.MachineId === machineId,
			);

			if (filteredConnections.value.length === 0) {
				errorMessage.value =
					"No active connections found for Machine ID: " + machineId;
			} else {
				errorMessage.value = undefined;
			}
		} else {
			filteredConnections.value = activeConnectionWithOrganization.value || [];
			errorMessage.value = undefined;
		}
	});
</script>

<template>
	<div class="box-border flex min-h-0 flex-col gap-4 pl-5">
		<div class="flex">
			<PageHeader title="Active ECS Tasks" />

			<button
				@click="openDisclaimerDialog()"
				class="mx-5 mt-8 flex h-10 w-10 items-center justify-center rounded-full bg-bright-orange text-white"
			>
				<i class="material-icons">info </i>
			</button>
		</div>

		<ListConnection
			:tasks="filteredConnections"
			:type="type"
			:isLoading="activeConnection.isLoading.value"
			@terminateConnection="TerminateConnection"
			:errorMessage="errorMessage"
		/>
		<ConfirmDialog ref="confirmDialog" />
		<EndConnection ref="deleteDialog" />
		<Disclaimer ref="disclaimerDialog" />
	</div>
</template>
